import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { AlertTriangle, Bell, Camera, CheckCircle2, Clock } from 'lucide-react'
import {
  Card, CardHeader, Dialog, DialogContent, DialogTitle, EmptyState, ErrorState,
  LoadingState, Identifier, NoPermissionState, useLocale,
} from '@hk/ui'
import {
  ApiError, changeIssueState, changeRequestState, fetchAttachmentBlobUrl,
  fetchAttachments, fetchIssues, fetchRequests,
  fetchFaultCategories,
  formatCount, formatDateTime, formatTime, may, needsReason, nameIn, translateError,
  type Attachment, type IssueView, type RequestView,
} from '@hk/core'
import { STRINGS } from '../i18n/strings'
import { FaultDetail } from './FaultDetail'

/* ============================================================================
   W-60 + W-70 · ISSUES AND REQUESTS — two lists that are deliberately not one list,
   on one screen that is deliberately not two screens.

   THE APPROVED PACKS DRAW THESE AS TWO SCREENS: W-60 the request inbox and W-70 the
   fault register. They are one screen here because a floor supervisor asks "what is
   outstanding on my floor?" once and not twice, and two screens means two tabs, two
   refresh cycles and two places to miss something at seven in the morning.

   That is a change to an approved design pack, so it is CHANGE NOTE 02 rather than a
   quiet difference between the design and the build — see SCREENS.md.

   They look alike on screen and they are not the same thing:

     an ISSUE   is something broken   — it may have taken the room off sale
     a REQUEST  is something promised — it can be LATE, and a guest is waiting

   The temptation is one "service items" list with a type column. It would be shorter
   and it would answer neither question well: a supervisor scanning for "what is
   costing us a room tonight" and a supervisor scanning for "who is waiting" are doing
   two different jobs at two different moments, and a single sorted list forces both to
   filter before they can start.

   So: two columns, each sorted by ITS OWN urgency. Issues by severity, because a
   blocking fault outranks everything. Requests by promise, because the one closest to
   breaking is the one to do next.
   ========================================================================= */

export function ServiceBoard({ propertyId }: { propertyId: string }) {
  const { locale } = useLocale()
  const t = STRINGS[locale]
  const queryClient = useQueryClient()
  const [reasonFor, setReasonFor] = useState<{ kind: 'issue' | 'request'; id: string } | null>(null)
  const [reason, setReason] = useState('')

  /* **W-71 — ONE FAULT, IN FULL** (Sprint 64). A card answers "what is wrong"; opening it
     answers "why is the room still off sale, and who has touched it". It REPLACES the
     board rather than opening over it, the same way W-21 replaced the room dialog: a
     screen with its own history, its own acts and its own refusals is a screen, and
     putting one in a dialog is how the room dialog quietly became three of them. */
  const [openFault, setOpenFault] = useState<string>()

  /* **THE NAMES COME FROM THE HOTEL, NOT FROM THIS FILE** (W-75, Sprint 63). They were a
     dictionary here and a DIFFERENT dictionary on the handset — `PLUMBING` read "Plumbing"
     on this screen and "Water or drain" on the phone, for the same fault. One list, on the
     server, which the hotel can correct.

     Retired categories are not in it, and that is right: the map is for DRAWING, and a
     fault raised last year under a category since withdrawn falls back to its code rather
     than vanishing. Six rows, cached by the query client, fetched once. */
  const faultCategories = useQuery({
    queryKey: ['fault-categories', propertyId],
    queryFn: ({ signal }) => fetchFaultCategories(propertyId, { signal }),
    staleTime: 5 * 60 * 1000,
  })

  const categoryName = (code: string) => {
    const found = faultCategories.data?.find((c) => c.code === code)
    if (!found) return code
    return nameIn(locale, found.name, found.nameAr)
  }

  const issues = useQuery({
    queryKey: ['issues', propertyId],
    queryFn: ({ signal }) => fetchIssues(propertyId, { signal }),
    refetchInterval: 30_000,
    retry: false,
  })

  const requests = useQuery({
    queryKey: ['requests', propertyId],
    queryFn: ({ signal }) => fetchRequests(propertyId, { signal }),
    // Sixty seconds, not thirty. LATENESS IS DERIVED ON THE SERVER, so a row can go
    // late without anything changing in the database — the refresh is the only thing
    // that makes it turn. A minute is close enough for a promise measured in tens of
    // minutes and halves the traffic of the room board.
    refetchInterval: 60_000,
    retry: false,
  })

  const resolveIssue = useMutation({
    mutationFn: (input: { issueId: string; reason?: string }) =>
      changeIssueState(input.issueId, {
        toState: 'RESOLVED',
        ...(input.reason ? { reason: input.reason } : {}),
      }),
    onSuccess: () => {
      // The ROOM BOARD is invalidated too, and that is the point of the whole feature:
      // clearing a blocking fault puts a room back on sale, so a board left showing it
      // out of order would be wrong within a second of this succeeding.
      void queryClient.invalidateQueries({ queryKey: ['issues', propertyId] })
      void queryClient.invalidateQueries({ queryKey: ['room-board', propertyId] })
      setReasonFor(null); setReason('')
    },
  })

  const fulfilRequest = useMutation({
    mutationFn: (requestId: string) => changeRequestState(requestId, { toState: 'FULFILLED' }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['requests', propertyId] }),
  })

  const sortedIssues = useMemo(() => {
    const rank = { BLOCKING: 0, MAJOR: 1, MINOR: 2 } as const
    return [...(issues.data ?? [])].sort((a, b) =>
      rank[a.severity] - rank[b.severity]
      || a.reportedAtUtc.localeCompare(b.reportedAtUtc))
  }, [issues.data])

  const sortedRequests = useMemo(
    () => [...(requests.data ?? [])].sort((a, b) => a.promisedByUtc.localeCompare(b.promisedByUtc)),
    [requests.data])

  /* BEFORE THE LOADING AND ERROR BRANCHES, deliberately. A fault opened and then resolved
     invalidates the issues query, which puts the board back into `isLoading` — and a
     detail screen that disappeared under the hand that had just pressed Resolve would be
     the exact behaviour W-21 was built to stop. */
  if (openFault) {
    return (
      <FaultDetail
        issueId={openFault}
        propertyId={propertyId}
        onClose={() => setOpenFault(undefined)}
      />
    )
  }

  if (issues.isLoading && !issues.error) return <LoadingState rows={5} />

  if (issues.error && !issues.data) {
    if (issues.error instanceof ApiError && issues.error.code === 'permission.denied')
      return <NoPermissionState />
    const translated = translateError(issues.error, locale)
    return (
      <ErrorState
        body={translated.message}
        onRetry={issues.refetch}
        {...(translated.correlationId ? { correlationId: translated.correlationId } : {})}
      />
    )
  }

  return (
    <div className="grid gap-3.5 lg:grid-cols-2">

      <Card>
        <CardHeader
          title={t.issuesTitle}
          icon={<AlertTriangle className="size-4" />}
          /* ONE NUMBER, ONE SOURCE: the count is the length of the list drawn below
             it, never a total sent alongside. The first time the two differ by one
             row, nobody believes either of them again. */
          actions={<span className="tnum text-[12px] text-muted-fg">{sortedIssues.length}</span>}
        />
        <div className="flex flex-col gap-1.5 p-2.5">
          {sortedIssues.length === 0 && <EmptyState title={t.issuesEmpty} />}
          {sortedIssues.map((issue) => (
            <IssueRow
              categoryName={categoryName}
              key={issue.issueId}
              issue={issue}
              locale={locale}
              propertyId={propertyId}
              onOpen={() => setOpenFault(issue.issueId)}
              onResolve={() => {
                // Ask for the reason BEFORE acting, so the box appears with the action
                // rather than after a refusal comes back. The server checks again
                // regardless — this is about not wasting somebody's time.
                if (needsReason('fault.downgradeBlocking') && issue.severity === 'BLOCKING')
                  setReasonFor({ kind: 'issue', id: issue.issueId })
                else resolveIssue.mutate({ issueId: issue.issueId })
              }}
              canResolve={issue.severity !== 'BLOCKING' || may('fault.downgradeBlocking')}
            />
          ))}
        </div>
      </Card>

      <Card>
        <CardHeader
          title={t.requestsTitle}
          icon={<Bell className="size-4" />}
          actions={<span className="tnum text-[12px] text-muted-fg">{sortedRequests.length}</span>}
        />
        <div className="flex flex-col gap-1.5 p-2.5">
          {sortedRequests.length === 0 && <EmptyState title={t.requestsEmpty} />}
          {sortedRequests.map((r) => (
            <RequestRow
              key={r.requestId}
              request={r}
              locale={locale}
              onFulfil={() => fulfilRequest.mutate(r.requestId)}
            />
          ))}
        </div>
      </Card>

      {reasonFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4">
          <div role="dialog" aria-modal="true" className="w-full max-w-sm rounded-hk border border-border bg-card p-4">
            <h2 className="text-sm font-semibold text-foreground">{t.reasonTitle}</h2>
            <p className="mt-1 text-[12px] text-muted-foreground">{t.reasonBody}</p>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              dir="auto"
              rows={3}
              className="mt-2.5 w-full rounded-hk border border-border bg-background p-2 text-[13px]"
            />
            <div className="mt-3 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => { setReasonFor(null); setReason('') }}
                className="rounded-hk border border-border px-3 py-1.5 text-[13px] cursor-pointer"
              >
                {t.cancel}
              </button>
              <button
                type="button"
                disabled={reason.trim() === ''}
                onClick={() => resolveIssue.mutate({ issueId: reasonFor.id, reason: reason.trim() })}
                className="rounded-hk bg-primary px-3 py-1.5 text-[13px] font-medium text-primary-foreground disabled:opacity-50 cursor-pointer"
              >
                {t.save}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

function IssueRow(
  { issue, locale, propertyId, onResolve, canResolve, categoryName, onOpen }:
  {
    issue: IssueView
    locale: 'en' | 'ar'
    propertyId: string
    onResolve: () => void
    canResolve: boolean
    /** Open W-71 on this fault. */
    onOpen: () => void
    /** **THE HOTEL'S OWN WORD FOR THE CATEGORY** (W-75). Passed in rather than looked up
     *  here so the list is fetched once for the board rather than once per row. */
    categoryName: (code: string) => string
  },
) {
  const t = STRINGS[locale]
  const blocking = issue.severity === 'BLOCKING'
  const [showPhotos, setShowPhotos] = useState(false)

  return (
    <article
      className={`rounded-hk border p-2.5 ${
        blocking
          ? 'border-[#fca5a5] bg-[#fef2f2] dark:border-[#7f1d1d] dark:bg-[#2a0f0f]'
          : 'border-border bg-background'}`}
    >
      <div className="flex items-start gap-2">
        {/* The room number is an IDENTIFIER — Western digits in both languages,
            because it is painted on the door and read aloud. (D-01) */}
        {issue.roomNumber && <Identifier>{issue.roomNumber}</Identifier>}
        <span className="min-w-0 flex-1">
          {/* **THE NAME IS THE WAY IN**, and it is a button rather than the whole card
              being clickable: the card already carries Photos and Resolve, and a card that
              is itself a control with two controls inside it is a keyboard trap and an
              accessibility fault. The name is the thing somebody reads first and the thing
              they would point at. */}
          <button
            type="button"
            onClick={onOpen}
            className="block text-start text-[13px] font-medium text-foreground underline
                       decoration-transparent underline-offset-2 hover:decoration-inherit
                       cursor-pointer"
          >
            {categoryName(issue.categoryCode)}
          </button>
          {issue.description && (
            /* Their words, never translated and never re-flowed. `dir="auto"` because
               a report typed in Arabic inside an English page must not read backwards. */
            <bdi dir="auto" className="block text-[12px] text-muted-foreground">
              {issue.description}
            </bdi>
          )}
          <span className="mt-0.5 block text-[11.5px] text-muted-foreground">
            <bdi>{nameIn(locale, issue.reportedByName, issue.reportedByNameAr)}</bdi>
            {' · '}
            {formatTime(new Date(issue.reportedAtUtc), locale)}
          </span>
        </span>
        <span className={`shrink-0 rounded px-1.5 py-0.5 text-[11px] font-medium ${
          blocking ? 'bg-[#b91c1c] text-white' : 'bg-muted text-muted-foreground'}`}
        >
          {t.severities[issue.severity]}
        </span>
      </div>

      {blocking && issue.tookPlaceOutOfOrder && (
        <p className="mt-1.5 text-[11.5px] font-medium text-[#b91c1c] dark:text-[#fca5a5]">
          {t.issueTookRoomOut}
        </p>
      )}

      <div className="mt-2 flex items-center justify-end gap-2">
        {/* SHOWN ONLY WHEN THERE IS SOMETHING TO SEE. The count comes from the same
            query as the issue, so the badge and the viewer cannot disagree — and a
            camera icon with no photographs behind it is a click that goes nowhere. */}
        {issue.photoCount > 0 && (
          <button
            type="button"
            onClick={() => setShowPhotos(true)}
            className="inline-flex items-center gap-1.5 rounded-hk border border-border
                       px-2.5 py-1 text-[12px] hover:bg-muted cursor-pointer me-auto"
          >
            <Camera className="size-3.5" />
            {t.issuePhotos}
            <span className="text-muted-foreground">
              {formatCount(issue.photoCount, locale)}
            </span>
          </button>
        )}

        {showPhotos && (
          <PhotoViewer
            propertyId={propertyId}
            issue={issue}
            locale={locale}
            onClose={() => setShowPhotos(false)}
          />
        )}

        <button
          type="button"
          onClick={onResolve}
          disabled={!canResolve}
          title={canResolve ? undefined : t.issueNeedsOtherRole}
          className="inline-flex items-center gap-1.5 rounded-hk border border-border px-2.5 py-1
                     text-[12px] hover:bg-muted disabled:opacity-40 cursor-pointer"
        >
          <CheckCircle2 className="size-3.5" />
          {t.resolve}
        </button>
      </div>
    </article>
  )
}

function RequestRow(
  { request, locale, onFulfil }:
  { request: RequestView; locale: 'en' | 'ar'; onFulfil: () => void },
) {
  const t = STRINGS[locale]

  return (
    <article
      className={`rounded-hk border p-2.5 ${
        request.isLate
          ? 'border-[#fcd34d] bg-[#fffbeb] dark:border-[#b45309] dark:bg-[#2a1e08]'
          : 'border-border bg-background'}`}
    >
      <div className="flex items-start gap-2">
        <Identifier>{request.roomNumber}</Identifier>
        <span className="min-w-0 flex-1">
          <span className="block text-[13px] font-medium text-foreground">
            {t.requestCategories[request.categoryCode] ?? request.categoryCode}
            {request.quantity !== null && ` · ${request.quantity}`}
          </span>
          {request.notes && (
            <bdi dir="auto" className="block text-[12px] text-muted-foreground">{request.notes}</bdi>
          )}
        </span>
        <span className={`flex shrink-0 items-center gap-1 text-[11.5px] ${
          request.isLate ? 'font-semibold text-[#b45309] dark:text-[#fbbf24]' : 'text-muted-foreground'}`}
        >
          <Clock className="size-3" />
          {request.isLate ? t.late : formatTime(new Date(request.promisedByUtc), locale)}
        </span>
      </div>

      <div className="mt-2 flex justify-end">
        <button
          type="button"
          onClick={onFulfil}
          className="inline-flex items-center gap-1.5 rounded-hk border border-border px-2.5 py-1
                     text-[12px] hover:bg-muted cursor-pointer"
        >
          <CheckCircle2 className="size-3.5" />
          {t.fulfil}
        </button>
      </div>
    </article>
  )
}

/* ============================================================================
   THE PHOTOGRAPHS, and the reason this is not four lines of `<img src>`.

   The download needs the `Authorization` header, and a browser sends none on an image
   request. So each photograph is FETCHED and turned into an object URL — and every one
   of those URLs has to be revoked, or six full-size bitmaps stay in memory on a board
   left open all morning.

   FETCHED ONLY WHEN OPENED. A supervisor's board draws forty issue cards; loading the
   photographs for all of them would be hundreds of requests and tens of megabytes to
   answer a question nobody asked.
   ========================================================================= */
function PhotoViewer(
  { propertyId, issue, locale, onClose }:
  { propertyId: string; issue: IssueView; locale: 'en' | 'ar'; onClose: () => void },
) {
  const t = STRINGS[locale]

  const list = useQuery({
    queryKey: ['attachments', propertyId, 'ISSUE', issue.issueId],
    queryFn: ({ signal }) =>
      fetchAttachments(propertyId, 'ISSUE', issue.issueId, { signal }),
    retry: false,
  })

  return (
    <Dialog open onOpenChange={(next) => { if (!next) onClose() }}>
      {/* CAPPED AND SCROLLED, and this was found by looking at the render rather than
          at the code. Two photographs at 60vh each made the dialog taller than the
          window: the title bar and the close button sat ABOVE the top of the screen,
          nothing scrolled, and the only way out was the Escape key — which nobody who
          had just lost the close button would think to try.

          The HEADER is fixed and the LIST scrolls, rather than the whole panel
          scrolling, so the way out never leaves the screen however many photographs
          there are. */}
      <DialogContent
        className="flex max-h-[85vh] max-w-2xl flex-col"
        aria-describedby={undefined}
      >
        <div className="flex shrink-0 items-center gap-2 border-b border-border p-3">
          <Camera className="size-4 text-primary" />
          <DialogTitle className="text-[13.5px] font-semibold">
            {t.issuePhotos}
            {issue.roomNumber ? <> · <Identifier>{issue.roomNumber}</Identifier></> : null}
          </DialogTitle>
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-3">
          {list.isLoading && <LoadingState rows={2} />}

          {list.error && (
            <ErrorState body={translateError(list.error, locale).message} onRetry={list.refetch} />
          )}

          {list.data?.map((photo) => (
            <Photo key={photo.fileObjectId} photo={photo} locale={locale} />
          ))}
        </div>
      </DialogContent>
    </Dialog>
  )
}

function Photo({ photo, locale }: { photo: Attachment; locale: 'en' | 'ar' }) {
  const t = STRINGS[locale]
  const [url, setUrl] = useState<string | null>(null)
  const [failure, setFailure] = useState<string | null>(null)

  useEffect(() => {
    let objectUrl: string | null = null
    let cancelled = false
    const controller = new AbortController()

    fetchAttachmentBlobUrl(photo.fileObjectId, { signal: controller.signal })
      .then((next) => {
        objectUrl = next
        // Revoke immediately if the component went away while the fetch was in flight —
        // otherwise the bitmap outlives the element that was going to show it.
        if (cancelled) URL.revokeObjectURL(next)
        else setUrl(next)
      })
      .catch((error: unknown) => {
        if (cancelled) return
        /* THE MISSING-FILE CASE GETS ITS OWN SENTENCE, and it says the fault is ours.

           A supervisor who opens a photograph and finds nothing there needs to know it
           is a storage failure rather than something they did — and needs a reference,
           because that is what turns "the photo is gone" into something somebody can
           investigate against the day the server moved. */
        const translated = translateError(error, locale)
        setFailure(
          error instanceof ApiError && error.code === 'file.missingFromStore'
            ? t.issuePhotoFailed.replace(
                '{correlationId}', translated.correlationId ?? '—')
            : translated.message)
      })

    return () => {
      cancelled = true
      controller.abort()
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [photo.fileObjectId, locale, t.issuePhotoFailed])

  return (
    <figure className="flex flex-col gap-1.5">
      {failure ? (
        <div
          role="alert"
          className="rounded-hk border border-[#fcd34d] bg-[#fffbeb] px-3 py-2 text-[12.5px]
                     text-[#b45309] dark:border-[#b45309] dark:bg-[#2a1e08] dark:text-[#fbbf24]"
        >
          {failure}
        </div>
      ) : url ? (
        <img
          src={url}
          alt=""
          className="max-h-[45vh] w-full rounded-hk border border-border object-contain"
        />
      ) : (
        <div className="h-40 w-full animate-pulse rounded-hk bg-muted" />
      )}

      <figcaption className="text-[11.5px] text-muted-foreground">
        {/* THE CAPTURE TIME WHERE THERE IS ONE, because that is when the fault looked
            like this — not when a signal finally reached the server. */}
        {t.issuePhotoTaken.replace(
          '{when}',
          formatDateTime(new Date(photo.capturedAtUtc ?? photo.uploadedAtUtc), locale))}
        {' · '}
        {t.issuePhotoBy.replace(
          '{name}', nameIn(locale, photo.uploadedByName, photo.uploadedByNameAr))}
      </figcaption>
    </figure>
  )
}
